<template>
  <div class="workspace__container__view">
    <div class="workspace__container__view__header">
      <div class="workspace__container__view__header__filter">
        <div class="container__inputs">
          <div class="settings-input log-editor__station">
            <label for="station">{{$t("workspace.log_editor.station")}}</label>
            <Multiselect
              v-model="searchModel.stationId"
              :searchable="true"
              :canDeselect="false"
              :canClear="false"
              track-by="name"
              valueProp="id"
              label="name"
              :options="listStations"
              @select="loadLog" />
          </div>
          <div class="settings-input log-editor__date">
            <label for="date">{{$t("workspace.log_editor.date")}}</label>
            <input type="date" id="date" v-model="searchModel.date" @change="loadLog">
          </div>
        </div>
      </div>
      <div class="workspace__container__view__header__filter">
        <div class="container__inputs">
          <div class="settings-input log-editor__search-input">
            <label for="spotSearch">{{$t("workspace.log_editor.search_spot")}}</label>
            <Multiselect
              id="spotSearch"
              v-model="selectedSpotName"
              :searchable="true"
              :canClear="true"
              :disabled="!log"
              :options="spotNames"
              :placeholder="$t('workspace.log_editor.search_spot_placeholder')"
              :noOptionsText="$t('workspace.log_editor.no_matches')"
              :noResultsText="$t('workspace.log_editor.no_matches')" />
          </div>
          <div class="log-editor__search-nav" v-if="selectedSpotName">
            <span class="log-editor__search-count">
              {{matches.length > 0 ? $t('workspace.log_editor.match_count', {current: currentMatchIndex + 1, total: matches.length}) : $t('workspace.log_editor.no_matches')}}
            </span>
            <button type="button" class="log-editor__nav-button" :disabled="matches.length === 0" :title="$t('workspace.log_editor.previous_match')" @click="prevMatch">
              <i class="material-icons">keyboard_arrow_up</i>
            </button>
            <button type="button" class="log-editor__nav-button" :disabled="matches.length === 0" :title="$t('workspace.log_editor.next_match')" @click="nextMatch">
              <i class="material-icons">keyboard_arrow_down</i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="generate--info" v-if="!hasSearched">
      <div>
        <i class="material-icons">info</i>{{$t('workspace.log_editor.none_selected')}}
      </div>
    </div>
    <div class="generate--info" v-else-if="!hadError && !log">
      <div>
        <i class="material-icons">info</i>{{$t('workspace.log_editor.none_result')}}
      </div>
    </div>
    <div class="generate--error" v-else-if="hadError">
      <div>
        <i class="material-icons">info</i>{{$t('workspace.log_editor.load_error')}}
      </div>
    </div>
    <section v-else-if="log" class="log-editor__content">
      <div class="table-scroll">
        <table class="workspace__container__view__table with-padding">
          <thead>
            <tr>
              <th class="column-xs">#</th>
              <th class="column-m">{{$t("workspace.log_editor.time")}}</th>
              <th>{{$t("workspace.log_editor.description")}}</th>
              <th class="column-m">{{$t("workspace.log_editor.duration")}}</th>
            </tr>
          </thead>
          <tbody v-for="(group, groupIndex) in groupedEvents" :key="groupIndex" :class="{'log-editor__group--block': group.header}">
            <tr v-if="group.header" class="log-editor__row--block-header">
              <td>{{parseParams(group.header).Number}}</td>
              <td>{{formatTime(group.header)}}</td>
              <td>
                {{$t('workspace.log_editor.commercial_block')}}
                <span class="pill" v-if="parseParams(group.header).Mode">({{parseParams(group.header).IsHard ? $t('workspace.log_editor.commercial_block_hard') : $t('workspace.log_editor.commercial_block_soft')}})</span>
              </td>
              <td>{{formatDuration(parseParams(group.header).CommercialSeconds)}}</td>
            </tr>

            <tr
              v-for="event in group.items"
              :key="event.secuencerOrder"
              :id="'log-event-' + event.secuencerOrder"
              :class="{
                'log-editor__row--block-item': group.header,
                'log-editor__row--match': isMatch(event),
                'log-editor__row--match-current': isCurrentMatch(event)
              }">
              <td></td>
              <td>{{formatTime(event)}}</td>
              <td>
                <template v-if="event.customCommandType === 'RND'">
                  {{parseParams(event).Command === 'ROT' ? $t('workspace.log_editor.rot') : $t('workspace.log_editor.rnd')}}
                  — {{parseParams(event).Category}} x{{parseParams(event).Qty}}<template v-if="parseParams(event).Prefix"> ({{parseParams(event).Prefix}})</template>
                </template>
                <template v-else-if="event.customCommandType === 'COMMENT'">
                  <span class="log-editor__comment">
                    <i class="material-icons">chat_bubble_outline</i>
                    {{parseParams(event).Text}}
                  </span>
                </template>
                <template v-else-if="event.customCommandType === 'LIVETEXT'">
                  <strong>{{parseParams(event).Title}}</strong>: {{parseParams(event).Text}}
                </template>
                <template v-else-if="event.relatedAsset">
                  <i class="material-icons log-editor__error-icon" v-if="event.relatedAsset.status !== 0" :title="event.customTitle || event.relatedAsset.name">error</i>
                  <!-- <span :class="{'log-editor__link': event.relatedAsset.spotId}" @click="event.relatedAsset.spotId && openSpot(event.relatedAsset.spotId)"> -->
                  <span>
                    {{commandLabel(event.relatedAsset.name)}}
                  </span>
                </template>
                <template v-else>
                  {{event.customTitle || event.rawLine}}
                </template>
              </td>
              <td>{{formatDuration(event.commandLength)}}</td>
            </tr>

            <tr v-if="group.end" class="log-editor__row--block-end">
              <td></td>
              <td>{{formatTime(group.end)}}</td>
              <td>{{$t('workspace.log_editor.commercial_block_end')}}</td>
              <td></td>
            </tr>
          </tbody>
          <tbody v-if="log.events.length == 0">
            <tr>
              <td colspan="4">{{$t('workspace.log_editor.none_result')}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div></div>
    </section>
  </div>
</template>
<script>
import { ref } from '@vue/reactivity';
import { useStore } from 'vuex';
import { computed, onMounted, onUnmounted, nextTick, watch } from '@vue/runtime-core';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import Multiselect from '@vueform/multiselect';

export default {
  components: {
    Multiselect
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();

    const listStations = computed(() => store.getters.station_stations({name: ''}));
    const searchModel = ref({
      stationId: null,
      date: ''
    });
    const log = ref(null);
    const hasSearched = ref(false);
    const hadError = ref(false);

    const selectedSpotName = ref(null);
    const currentMatchIndex = ref(0);

    const spotNames = computed(() => {
      if (!log.value) return [];
      const names = new Set();
      for (const event of log.value.events) {
        if (event.relatedAsset) names.add(event.relatedAsset.name);
      }
      return [...names].sort();
    });

    const matches = computed(() => {
      if (!log.value || !selectedSpotName.value) return [];
      return log.value.events
        .filter(event => event.relatedAsset && event.relatedAsset.name === selectedSpotName.value)
        .map(event => event.secuencerOrder);
    });

    function isMatch(event) {
      return !!selectedSpotName.value && !!event.relatedAsset && event.relatedAsset.name === selectedSpotName.value;
    }

    function isCurrentMatch(event) {
      return matches.value[currentMatchIndex.value] === event.secuencerOrder;
    }

    function scrollToCurrentMatch() {
      if (matches.value.length === 0) return;
      const el = document.getElementById('log-event-' + matches.value[currentMatchIndex.value]);
      if (el) el.scrollIntoView({block: 'center', behavior: 'smooth'});
    }

    function goToMatch(delta) {
      if (matches.value.length === 0) return;
      currentMatchIndex.value = (currentMatchIndex.value + delta + matches.value.length) % matches.value.length;
      scrollToCurrentMatch();
    }

    function nextMatch() {
      goToMatch(1);
    }

    function prevMatch() {
      goToMatch(-1);
    }

    function onKeyDown(e) {
      if (e.key !== 'F3') return;
      if (!selectedSpotName.value || matches.value.length === 0) return;
      e.preventDefault();
      if (e.shiftKey) prevMatch();
      else nextMatch();
    }

    watch(selectedSpotName, async () => {
      currentMatchIndex.value = 0;
      await nextTick();
      scrollToCurrentMatch();
    });

    onMounted(async () => {
      window.addEventListener('keydown', onKeyDown);
      store.dispatch('loading_setActive', true);
      await store.dispatch('station_getAll');

      const extendedData = store.getters.tabs_getActiveExtendedData;
      if (extendedData && 'stationId' in extendedData) {
        searchModel.value = {...extendedData};
      } else {
        searchModel.value.stationId = listStations.value.length > 0 ? listStations.value[0].id : null;
        searchModel.value.date = moment().format('YYYY-MM-DD');
      }
      await loadLog();

      store.dispatch('loading_setActive', false);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', onKeyDown);
    });

    async function loadLog() {
      if (!searchModel.value.stationId || !searchModel.value.date) return;

      store.dispatch('loading_setActive', true);
      hasSearched.value = true;
      hadError.value = false;
      log.value = null;
      selectedSpotName.value = null;

      const result = await store.dispatch('trafficLogFile_get', {
        stationId: searchModel.value.stationId,
        date: searchModel.value.date
      });

      if (result.ok) {
        log.value = result.data ? {...result.data, events: [...result.data.events].sort((a, b) => a.secuencerOrder - b.secuencerOrder)} : null;
      } else {
        hadError.value = true;
      }

      await store.dispatch('tabs_updateTab', {id: route.params.id, extendedData: {...searchModel.value}});
      store.dispatch('loading_setActive', false);
    }

    const groupedEvents = computed(() => {
      if (!log.value) return [];
      const groups = [];
      let current = null;

      for (const event of log.value.events) {
        if (event.customCommandType === 'CommercialBlock') {
          current = {header: event, items: [], end: null};
          groups.push(current);
        } else if (event.customCommandType === 'CommercialBlockEnd') {
          if (current) current.end = event;
          current = null;
        } else if (current) {
          current.items.push(event);
        } else {
          groups.push({header: null, items: [event], end: null});
        }
      }

      return groups;
    });

    function parseParams(event) {
      return event.customCommandParams ? JSON.parse(event.customCommandParams) : {};
    }

    function formatTime(event) {
      const pad = n => String(n).padStart(2, '0');
      return `${pad(event.hour)}:${pad(event.minute)}:${pad(event.second)}`;
    }

    function formatDuration(seconds) {
      if (seconds == null) return '';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
    }

    function commandLabel(name) {
      const key = `commands.${name}`;
      return t(key) !== key ? t(key) : name;
    }

    async function openSpot(spotId) {
      const tab = {
        id: spotId,
        module: 'spot_programming',
        name: 'spot/detail',
        extendedData: {}
      };

      await store.dispatch('tabs_addTab', tab);
      await store.dispatch('tabs_setActive', tab);
      router.push(`/${tab.module}/${tab.name}/${tab.id}`);
    }

    watch(() => route.params.id, () => {
      const extendedData = store.getters.tabs_getActiveExtendedData;
      if (extendedData && 'stationId' in extendedData) {
        searchModel.value = {...extendedData};
        loadLog();
      }
    });

    return {
      listStations,
      searchModel,
      log,
      hasSearched,
      hadError,
      groupedEvents,
      parseParams,
      formatTime,
      formatDuration,
      commandLabel,
      openSpot,
      loadLog,
      selectedSpotName,
      spotNames,
      matches,
      currentMatchIndex,
      isMatch,
      isCurrentMatch,
      nextMatch,
      prevMatch
    }
  }
}
</script>
<style lang="scss" scoped>
  @import '@/styles/_common.scss';
  @import '@/styles/_variables.scss';

  .workspace__container__view {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .workspace__container__view__header {
    flex-shrink: 0;
  }

  .container__inputs {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .log-editor__search-input {
    width: 280px;
    max-width: 900px;
    flex: 0 0 auto;

    :deep(.multiselect-dropdown) {
      left: auto;
      right: 0;
      width: max-content;
      max-width: #{'min(420px, calc(100vw - 48px))'};
    }

    :deep(.multiselect-option) {
      white-space: normal;
      word-break: break-word;
    }
  }

  .log-editor__search-nav {
    display: flex;
    align-items: center;
    align-self: flex-end;
    gap: 4px;
    height: 32px;
  }

  .log-editor__search-count {
    font-size: 14px;
    margin-right: 4px;
    white-space: nowrap;
  }

  .log-editor__nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid $lighter-gray-color;
    border-radius: 4px;
    background: transparent;
    color: #fff;
    cursor: pointer;

    &:disabled {
      opacity: .4;
      cursor: default;
    }

    i {
      font-size: 20px;
    }
  }

  .log-editor__station {
    width: 320px;
    max-width: 900px;
    flex: 0 0 auto;
  }

  .log-editor__date {
    width: 160px;
    max-width: 200px;
    flex: 0 0 auto;
  }

  .log-editor__content {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(700px, 1fr));
    gap: 16px;

    @media (max-width: 920px) {
      grid-template-columns: 1fr;
    }
  }

  .table-scroll {
    flex: 1;
    height: auto;
    min-height: 0;

    thead {
      z-index: 2;
    }

    th {
      position: relative;
      background-color: $darker-gray-color;
    }
  }

  .generate--info {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 20px;

    div {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: fit-content;
      padding: 24px;
      color: black;
      border: 1px solid #669df6;
      background: #d2e3fc;
      border-radius: 8px;

      i {
        color: #669df6;
      }
    }
  }

  .generate--error {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 20px;

    div {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: fit-content;
      padding: 24px;
      color: black;
      border: 1px solid #f82f2f;
      background: #fcd2d2;
      border-radius: 8px;

      i {
        color: #f66666;
      }
    }
  }

  .log-editor {
    &__group--block td {
      background: rgba(255, 255, 255, .05);
    }

    &__row--block-header td {
      font-weight: 600;
    }

    &__row--block-item td:nth-child(3) {
      padding-left: 32px;
    }

    &__row--block-end td {
      font-style: italic;
      opacity: .6;
    }

    &__error-icon {
      color: #f66666;
      vertical-align: middle;
      margin-right: 4px;
      cursor: help;
    }

    &__link {
      cursor: pointer;
      text-decoration: underline;
    }

    &__comment {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 1px solid #fff;
      border-radius: 4px;

      i {
        font-size: 18px;
        flex-shrink: 0;
      }
    }

    &__row--match td {
      background: rgba(96, 96, 96, .55);
    }

    &__row--match-current td {
      background: rgb(96, 96, 96);
      box-shadow: inset 0 0 0 1px rgb(96, 96, 96);
    }
  }
</style>
